/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { routes } from '@/route-paths';
import { cn } from '@/lib/utils';
import {
    type ValidationMethod,
    type ValidationMethodType,
    isValidationMethodType,
} from '@/extensions/priceshoes/lib/auth/validation-method';

interface ValidationMethodFormProps {
    /** Partner ID or email identified as PENDING by Auth Extension (PRIC-165). */
    identifier: string;
    /** Methods Auth Extension returned for the shopper; only these are shown (CA4). */
    validations: ValidationMethod[];
    /** Method chosen before the last submit, to keep it selected. */
    selectedMethod?: ValidationMethodType;
    /** Methods shown but not selectable (e.g. security questions once their attempts ran out). */
    disabledMethods?: ValidationMethodType[];
    /**
     * Soci@ ID of a BLOCKED shopper (security questions attempts ran out, PRIC-168 CA8). Shows the "update your
     * contact details" variant: Auth Extension only returns email and phone for them.
     */
    partnerId?: string;
    /** Error resolved by the login action. */
    error?: string;
    returnUrl?: string | null;
}

/** Header of the "update your contact details" variant (BLOCKED shopper). */
function ContactUpdateHeader({ partnerId }: { partnerId: string }): ReactElement {
    const { t } = useTranslation('extPriceshoes');

    return (
        <div className="space-y-4 text-center">
            <h1 className="text-2xl font-semibold text-foreground">{t('login.contactUpdate.title')}</h1>
            <p className="text-foreground">{t('login.contactUpdate.description')}</p>
            {partnerId ? (
                <p className="inline-block rounded-ui bg-muted px-4 py-2 font-semibold text-foreground">
                    {t('login.contactUpdate.partnerId', { value: partnerId })}
                </p>
            ) : null}
            <p className="text-foreground">{t('login.contactUpdate.help')}</p>
            <div className="flex items-center gap-4 text-muted-foreground" aria-hidden="true">
                <span className="h-px flex-1 bg-border" />
                {t('login.contactUpdate.or')}
                <span className="h-px flex-1 bg-border" />
            </div>
            <p className="text-foreground">{t('login.contactUpdate.chooseMethod')}</p>
        </div>
    );
}

/**
 * Identity validation screen for a shopper that exists in PriceSoft but has no password yet (PRIC-167):
 * the shopper picks how to validate their identity (code to email, code by SMS or security questions)
 * before creating their password. Email and phone are shown masked, as Auth Extension returns them.
 */
export default function ValidationMethodForm({
    identifier,
    validations,
    selectedMethod,
    disabledMethods = [],
    partnerId,
    error,
    returnUrl,
}: ValidationMethodFormProps): ReactElement {
    const blocked = partnerId !== undefined;
    const { t } = useTranslation('extPriceshoes');
    const [method, setMethod] = useState<ValidationMethodType | undefined>(
        selectedMethod && !disabledMethods.includes(selectedMethod) ? selectedMethod : undefined
    );
    const [methodRequired, setMethodRequired] = useState(false);

    // Require a choice before submitting; the action validates again.
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (!method) {
            event.preventDefault();
            setMethodRequired(true);
        }
    };

    const fieldError = methodRequired ? t('login.validation.errors.empty') : error;

    return (
        <div className="space-y-6">
            {blocked ? (
                <ContactUpdateHeader partnerId={partnerId} />
            ) : (
                <div className="space-y-2">
                    <h1 className="text-2xl font-semibold text-foreground">{t('login.validation.title')}</h1>
                    <p className="text-foreground">{t('login.validation.description')}</p>
                </div>
            )}

            <Form method="post" className="space-y-6" noValidate onSubmit={handleSubmit}>
                <RadioGroup
                    name="validationMethod"
                    value={method}
                    onValueChange={(value) => {
                        if (isValidationMethodType(value)) {
                            setMethod(value);
                            setMethodRequired(false);
                        }
                    }}
                    aria-label={t('login.validation.title')}
                    aria-invalid={fieldError ? true : undefined}
                    aria-describedby={fieldError ? 'validation-method-error' : undefined}>
                    {validations.map(({ type, value }) => (
                        <Label
                            key={type}
                            htmlFor={`validation-method-${type}`}
                            className={cn(
                                'flex items-center gap-3 rounded-ui border border-border p-4 font-normal',
                                disabledMethods.includes(type) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            )}>
                            <RadioGroupItem
                                id={`validation-method-${type}`}
                                value={type}
                                disabled={disabledMethods.includes(type)}
                            />
                            <span className="flex flex-col gap-1">
                                <span className="text-foreground">{t(`login.validation.methods.${type}`)}</span>
                                {value ? <span className="text-sm text-muted-foreground">{value}</span> : null}
                            </span>
                        </Label>
                    ))}
                </RadioGroup>

                {fieldError ? (
                    <p id="validation-method-error" role="alert" className="text-sm text-destructive">
                        {fieldError}
                    </p>
                ) : null}

                {/* Posted back so the screen can be shown again; Commerce Cloud validates the method on its side. */}
                <input type="hidden" name="identifier" value={identifier} />
                <input type="hidden" name="validations" value={JSON.stringify(validations)} />
                <input type="hidden" name="disabledMethods" value={disabledMethods.join(',')} />
                {blocked ? <input type="hidden" name="partnerId" value={partnerId} /> : null}
                <input type="hidden" name="loginMode" value="priceshoesValidationMethod" />
                {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}

                <FormSubmitButton defaultText={t('login.next')} submittingText={t('login.submitting')} />
            </Form>

            <div className="space-y-4 border-t border-border pt-6">
                {blocked ? (
                    <Button asChild variant="outline" className="w-full">
                        <Link to={routes.home}>{t('login.contactUpdate.home')}</Link>
                    </Button>
                ) : null}
                {/* A plain navigation to /login drops the action data and goes back to the identification step. */}
                <Button asChild variant={blocked ? 'link' : 'outline'} className="w-full">
                    <Link to={routes.login}>{t('login.password.otherAccount')}</Link>
                </Button>
            </div>
        </div>
    );
}
